/**
 * How the agent interviews a client — the adviser's choice (2026-10-22):
 *   Brain  — the agent picks each next question from the adviser's routes, follows the client's goal.
 *   Fixed  — the adviser's own questionnaire, in their order, nothing else (agencies, clinics, set intake forms).
 * Saved changes apply to the next message of every conversation, so switching asks for confirmation.
 * The builder can also propose an interview (kind "interview") from a questionnaire document.
 *
 * API: GET /agent-builder/interview · PUT /agent-builder/interview
 *      { mode, questions: [{ text, section?, optionSpecific? }], verbatim, acknowledgements, optionQuestions, closingMessage, rules, maxQuestions }
 * In fixed mode the agent's instructions are only: a short platform base (format, language), the questions, these
 * settings and the adviser's own rules — no long directive to maintain.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../Context/ToastContext';
import { Box, Row, Muted, Help, SmallBtn, PrimaryBtn, GhostBtn, DangerBtn, Input, TextArea, Empty, errText, fmtWhen } from './styles';

const MODES = [
  { id: 'brain', title: 'Brain — smart interview', text: 'Your agent picks each next question from your routes, follows what the client wants, skips what does not apply and can point to another route that fits better.' },
  { id: 'fixed', title: 'Fixed questions', text: 'Your agent asks your own list of questions, in your order, and nothing else. It skips what the client already answered. For agencies, clinics and anyone with a set intake form.' },
];

const Toggle = ({ on, onChange, label, help }) => (
  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '8px 0', cursor: 'pointer' }}>
    <input type="checkbox" checked={!!on} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
    <span><span style={{ fontSize: 13 }}>{label}</span>{help && <Help style={{ marginTop: 2 }}>{help}</Help>}</span>
  </label>
);

const strip = (v) => ({
  mode: v.mode, questions: (v.questions || []).map(q => ({ text: q.text, ...(q.section ? { section: q.section } : {}), ...(q.optionSpecific ? { optionSpecific: true } : {}) })),
  verbatim: v.verbatim !== false, acknowledgements: v.acknowledgements !== false, optionQuestions: v.optionQuestions !== false,
  closingMessage: v.closingMessage || '', rules: v.rules || '', maxQuestions: v.maxQuestions || '',
});

export default function InterviewPanel({ api, hasRoutes, onSaved }) {
  const { showToast } = useToast();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { const r = await api.get('/agent-builder/interview'); setSaved(r.data); setDraft(strip(r.data)); setError(''); } catch (e) { setError(errText(e, 'Could not load the interview')); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => saved && draft && JSON.stringify(strip(saved)) !== JSON.stringify(draft), [saved, draft]);
  if (error) return <Empty>{error}</Empty>;
  if (!draft) return <Empty>Loading…</Empty>;

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const setQ = (i, patch) => set({ questions: draft.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const move = (i, by) => {
    const qs = [...draft.questions];
    const j = i + by;
    if (j < 0 || j >= qs.length) return;
    [qs[i], qs[j]] = [qs[j], qs[i]];
    set({ questions: qs });
  };
  const addPasted = () => {
    const lines = paste.split('\n').map(l => l.replace(/^\s*(\d+[.)]|[-*•])\s*/, '').trim()).filter(l => l.length >= 5);
    if (!lines.length) return;
    set({ questions: [...draft.questions, ...lines.map(text => ({ text }))] });
    setPaste('');
  };

  const save = async () => {
    const live = saved.mode !== draft.mode;
    if (live && !window.confirm(draft.mode === 'fixed'
      ? 'From the next message, your agent will ask only your questions, in your order. Continue?'
      : 'From the next message, your agent will pick its questions from your routes again. Your list is kept. Continue?')) return;
    setBusy(true);
    try {
      const body = { ...draft, questions: draft.questions.filter(q => String(q.text || '').trim()), maxQuestions: draft.maxQuestions === '' ? undefined : Number(draft.maxQuestions) };
      const r = await api.put('/agent-builder/interview', body);
      setSaved(r.data); setDraft(strip(r.data));
      showToast('Saved — your agent uses it from the next message', 'success');
      onSaved?.();
    } catch (e) { showToast(errText(e, 'Could not save'), 'error'); } finally { setBusy(false); }
  };

  const fixed = draft.mode === 'fixed';
  return (
    <div>
      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>How your agent interviews a client</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => set({ mode: m.id })} style={{
              textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '10px 12px', color: '#e6edf3',
              background: draft.mode === m.id ? 'rgba(47,129,247,0.14)' : 'transparent',
              border: `1px solid ${draft.mode === m.id ? '#2f81f7' : 'rgba(255,255,255,0.12)'}`,
            }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{draft.mode === m.id ? '● ' : '○ '}{m.title}</div>
              <div style={{ color: '#8b98a5', fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>{m.text}</div>
            </button>
          ))}
        </div>
        {saved.updatedAt && <Help>Last changed {fmtWhen(saved.updatedAt)}{saved.updatedBy ? ` by ${saved.updatedBy}` : ''}. Now running: <b>{saved.mode === 'fixed' ? 'fixed questions' : 'brain'}</b>.</Help>}
      </Box>

      {(fixed || draft.questions.length > 0) && (
        <Box>
          <Row style={{ justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Your questions{!fixed && <Muted> — kept, used when you switch to fixed questions</Muted>}</div>
            <Muted>{draft.questions.length} question{draft.questions.length === 1 ? '' : 's'}</Muted>
          </Row>
          <Help style={{ marginBottom: 8 }}>
            Asked in this order. Name, email and phone are collected at the start anyway — a question about them is skipped.
            {hasRoutes ? ' Tick ◆ for a question that applies only to the option the client chose (a vacancy or a service): the agent makes it concrete with that route’s own questions.' : ''}
            {' '}Or drop your questionnaire into the chat — the builder proposes it word for word.
          </Help>
          {draft.questions.length === 0 && <Empty style={{ padding: 12 }}>No questions yet.</Empty>}
          {draft.questions.map((q, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '26px 1fr auto', gap: 8, alignItems: 'start', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <Muted style={{ paddingTop: 9, textAlign: 'right' }}>{i + 1}.</Muted>
              <div>
                <TextArea rows={Math.min(4, Math.max(1, Math.ceil(String(q.text).length / 80)))} value={q.text} onChange={e => setQ(i, { text: e.target.value })} />
                {hasRoutes && (
                  <label style={{ fontSize: 12, color: q.optionSpecific ? '#e3b341' : '#8b98a5', cursor: 'pointer', display: 'inline-flex', gap: 6, marginTop: 4 }}>
                    <input type="checkbox" checked={!!q.optionSpecific} onChange={e => setQ(i, { optionSpecific: e.target.checked })} />◆ only for the option the client chose
                  </label>
                )}
              </div>
              <Row style={{ flexWrap: 'nowrap', paddingTop: 4 }}>
                <SmallBtn title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</SmallBtn>
                <SmallBtn title="Move down" disabled={i === draft.questions.length - 1} onClick={() => move(i, 1)}>↓</SmallBtn>
                <DangerBtn title="Remove" onClick={() => set({ questions: draft.questions.filter((_, j) => j !== i) })}>✕</DangerBtn>
              </Row>
            </div>
          ))}
          <Row style={{ marginTop: 8 }}>
            <GhostBtn onClick={() => set({ questions: [...draft.questions, { text: '' }] })}>+ Add a question</GhostBtn>
          </Row>
          <div style={{ marginTop: 10 }}>
            <TextArea rows={3} placeholder="Paste several questions, one per line (numbers and bullets are removed)" value={paste} onChange={e => setPaste(e.target.value)} />
            <Row style={{ marginTop: 6 }}><SmallBtn disabled={!paste.trim()} onClick={addPasted}>Add these to the list</SmallBtn></Row>
          </div>
        </Box>
      )}

      {fixed && (
        <Box>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>How the questions are asked</div>
          <Toggle on={draft.verbatim} onChange={v => set({ verbatim: v })} label="Word for word" help="Off: the agent keeps the meaning but may phrase a question naturally." />
          <Toggle on={!draft.acknowledgements} onChange={v => set({ acknowledgements: !v })} label="No comments between questions" help="No “Thanks”, “Noted” or “Great” — straight to the next question." />
          {hasRoutes && <Toggle on={draft.optionQuestions} onChange={v => set({ optionQuestions: v })} label="Ask the chosen option’s own questions" help="When the client picks one of your routes (a vacancy, a service), its configured questions are asked too — in the ◆ places, or before the end." />}
          <div style={{ marginTop: 10 }}>
            <Muted>Closing message — exactly what the client reads at the end (optional)</Muted>
            <Input style={{ marginTop: 4 }} value={draft.closingMessage} onChange={e => set({ closingMessage: e.target.value })} placeholder="e.g. Thank you. Your information has been collected." />
          </div>
          <div style={{ marginTop: 12 }}>
            <Muted>Your rules for the agent — in your words: its role, what it must never say or do, tone (optional, up to 4000 characters)</Muted>
            <TextArea rows={7} style={{ marginTop: 4 }} value={draft.rules} onChange={e => set({ rules: e.target.value })}
              placeholder={'e.g. You collect information for our recruitment team. Never tell a candidate whether they qualify. Never recommend other positions or give career advice. If asked about pay, say the recruitment team handles it.'} />
            <Help>With fixed questions these rules and your questions are the agent’s whole instructions — keep them short and specific.</Help>
          </div>
          <Row style={{ marginTop: 10 }}>
            <Muted>Most questions in one conversation, clarifications included:</Muted>
            <Input type="number" min={10} max={60} style={{ width: 80 }} value={draft.maxQuestions} onChange={e => set({ maxQuestions: e.target.value })} />
          </Row>
        </Box>
      )}

      <Row style={{ justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: '#161b22', padding: '8px 0' }}>
        {dirty && <Muted>Unsaved changes</Muted>}
        <GhostBtn disabled={!dirty || busy} onClick={() => setDraft(strip(saved))}>Discard</GhostBtn>
        <PrimaryBtn disabled={!dirty || busy || (fixed && !draft.questions.some(q => String(q.text || '').trim()))} onClick={save}>{busy ? 'Saving…' : 'Save'}</PrimaryBtn>
      </Row>
    </div>
  );
}
